import React, { createContext, useCallback, useContext, useRef } from 'react'
import { flushSync } from 'react-dom'
import { QuestionPayload } from '../Schema/Test.schema'
import { Updater } from 'use-immer'

/** Imperative handle an essay editor registers so the submit paths can flush it before reading state. */
export type AnswerControl = { flush(): unknown; cancel(): void }

type TestProviderArgs = {
	mode: TestViewMode
	questions: Array<TestQuestion>
	drawerOpened: boolean
	openDrawer: React.Dispatch<boolean>
	overallProgress?: number
	test: Test
	formQuestions: Array<FormQuestion>
	setFormQuestions: Updater<Array<FormQuestion>>
	attempt: TestAttempt
	bottomSheetOpened: boolean
	openBottomSheet: React.Dispatch<boolean>
}
type TestContextArgs = TestProviderArgs & {
	/** Latest formQuestions, assigned during render — fresh right after a
	 * flushSync commit, unlike the closure values of callbacks created earlier. */
	formQuestionsRef: React.MutableRefObject<Array<FormQuestion>>
	registerAnswerControl: (questionId: number, control: AnswerControl | null) => void
	/** flushSync-wrapped: every registered editor emits its pending text and the
	 * resulting state is committed before this returns. Returns how many emitted.
	 * Only from event handlers or promise continuations — React refuses to flush
	 * inside render, layout or passive effects. */
	flushPendingAnswers: () => number
	/** Records an in-flight answer save so a submit can wait for it. Returns the same promise. */
	trackSave: <T>(promise: Promise<T>) => Promise<T>
	/** Resolves when every tracked save has settled, or after `capMs`. */
	settlePendingSaves: (capMs?: number) => Promise<void>
}
export type FormQuestion = TestQuestion & { touched: boolean; submitted: boolean }
const TestContext = createContext<TestContextArgs | null>(null)

const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

export const TestProvider: React.FC<TestProviderArgs> = ({ children, ...props }) => {
	const formQuestionsRef = useRef(props.formQuestions)
	formQuestionsRef.current = props.formQuestions
	const controls = useRef(new Map<number, AnswerControl>())
	const saves = useRef(new Set<Promise<unknown>>())

	const registerAnswerControl = useCallback((questionId: number, control: AnswerControl | null) => {
		if (control) controls.current.set(questionId, control)
		else controls.current.delete(questionId)
	}, [])

	const flushPendingAnswers = useCallback(() => {
		let emitted = 0
		flushSync(() => {
			controls.current.forEach(control => {
				if (control.flush() !== undefined) emitted += 1
			})
		})
		return emitted
	}, [])

	const trackSave = useCallback(<T,>(promise: Promise<T>): Promise<T> => {
		const settled: Promise<unknown> = promise.then(
			() => undefined,
			() => undefined
		)
		saves.current.add(settled)
		settled.then(() => saves.current.delete(settled))
		return promise
	}, [])

	// No Promise.allSettled: CRA4 ships no runtime polyfill for it.
	const settlePendingSaves = useCallback(async (capMs = 8000) => {
		const pending = Array.from(saves.current)
		if (!pending.length) return
		await Promise.race([Promise.all(pending), wait(capMs)])
	}, [])

	return (
		<TestContext.Provider value={{ ...props, formQuestionsRef, registerAnswerControl, flushPendingAnswers, trackSave, settlePendingSaves }}>
			{children}
		</TestContext.Provider>
	)
}

export const useTest = () => {
	const context = useContext(TestContext)
	if (!context) throw new Error('No TestProvider found !!!')
	return context
}
